import * as THREE from 'three';
import { DEFAULT_GAME_CONFIG } from '@/schemas';

export class GameScene {
  public scene: THREE.Scene;
  public camera: THREE.PerspectiveCamera;
  public renderer: THREE.WebGLRenderer;
  private canvas: HTMLCanvasElement;
  private backgroundObjects: THREE.Mesh[] = []; // 背景オブジェクト（雲など）

  // デバッグフラグ：trueにするとグリッドと軸が表示される
  private static readonly DEBUG_MODE = false;

  // 固定アスペクト比（16:9）
  private static readonly FIXED_ASPECT_RATIO = 16 / 9;

  constructor(canvas: HTMLCanvasElement) {
    this.canvas = canvas;

    // シーンの作成
    this.scene = new THREE.Scene();
    this.scene.background = new THREE.Color(0xf7f7f7); // Chrome Dino風の白/薄いベージュ

    // カメラの作成（16:9固定）
    this.camera = new THREE.PerspectiveCamera(75, GameScene.FIXED_ASPECT_RATIO, 0.1, 1000);

    const camPos = DEFAULT_GAME_CONFIG.cameraPosition;
    this.camera.position.set(camPos.x, camPos.y, camPos.z);
    this.camera.lookAt(0, 0, 0);

    // レンダラーの作成
    this.renderer = new THREE.WebGLRenderer({
      canvas: this.canvas,
      antialias: true,
    });

    // 16:9のアスペクト比を維持したキャンバスサイズを計算
    const { width, height } = this.calculateCanvasSize();
    this.renderer.setSize(width, height);
    this.renderer.setPixelRatio(window.devicePixelRatio);
    // Chrome Dino風のシンプルなスタイルなのでシャドウは無効
    this.renderer.shadowMap.enabled = false;

    // ライティングの設定
    this.setupLighting();

    // 地面の作成
    this.createGround();

    // グリッドの作成（デバッグ用）
    if (GameScene.DEBUG_MODE) {
      this.createGrid();
    }

    // 背景オブジェクト（雲）の作成
    this.createBackgroundObjects();

    // リサイズイベントリスナーの追加
    window.addEventListener('resize', this.handleResize.bind(this));
  }

  private setupLighting(): void {
    // Chrome Dino風のシンプルなライティング（均一な明るさ）
    const ambientLight = new THREE.AmbientLight(0xffffff, 1.0);
    this.scene.add(ambientLight);
  }

  private createGround(): void {
    // Chrome Dino風のシンプルな地面線
    const groundGeometry = new THREE.PlaneGeometry(100, 0.1);
    const groundMaterial = new THREE.MeshBasicMaterial({
      color: 0x535353, // グレー（Chrome Dino風）
    });

    const ground = new THREE.Mesh(groundGeometry, groundMaterial);
    ground.rotation.x = -Math.PI / 2; // 水平に配置
    ground.position.y = 0;
    this.scene.add(ground);
  }

  /**
   * グリッドの作成（デバッグ用）
   */
  private createGrid(): void {
    // XZ平面のグリッド（Y=0）
    const gridHelper = new THREE.GridHelper(20, 20, 0x0000ff, 0xcccccc);
    gridHelper.position.y = 0;
    this.scene.add(gridHelper);

    // 軸ヘルパー（X=赤、Y=緑、Z=青）
    const axesHelper = new THREE.AxesHelper(5);
    this.scene.add(axesHelper);
  }

  /**
   * 背景オブジェクト（雲など）を作成
   */
  private createBackgroundObjects(): void {
    // 雲を複数作成（地面より上の高さに配置）
    for (let i = 0; i < 10; i++) {
      const cloud = this.createCloud();
      // ランダムな位置に配置（地面より上、カメラより後ろ）
      cloud.position.set(
        (Math.random() - 0.5) * 50, // X: -25 〜 25
        2 + Math.random() * 3, // Y: 2 〜 5（地面より上）
        -5 - Math.random() * 10 // Z: -5 〜 -15（カメラより後ろ）
      );
      this.scene.add(cloud);
      this.backgroundObjects.push(cloud);
    }
  }

  /**
   * 雲のメッシュを作成（Chrome Dino風のシンプルな雲）
   */
  private createCloud(): THREE.Mesh {
    // シンプルな楕円形の雲
    const cloudGeometry = new THREE.SphereGeometry(0.4, 8, 8);
    const cloudMaterial = new THREE.MeshBasicMaterial({
      color: 0xd3d3d3, // 薄いグレー
    });

    const cloud = new THREE.Mesh(cloudGeometry, cloudMaterial);
    // 雲を横に伸ばす（スケール）
    cloud.scale.set(1.5, 0.6, 1);
    
    return cloud;
  }

  /**
   * 背景オブジェクトを更新（後ろに流れる）
   */
  public updateBackground(speed: number, deltaTime: number): void {
    for (const obj of this.backgroundObjects) {
      // 背景は地面のスピードより遅く流れる（視差効果）
      obj.position.x -= speed * deltaTime * 0.3; // 30%の速度で流れる

      // 画面左端に出たら右端に戻す
      if (obj.position.x < -25) {
        obj.position.x = 25;
        // 高さもランダムに変更
        obj.position.y = 2 + Math.random() * 3;
      }
    }
  }

  /**
   * 16:9のアスペクト比を維持しながら、ウィンドウに収まる最大サイズを計算
   */
  private calculateCanvasSize(): { width: number; height: number } {
    const windowWidth = window.innerWidth;
    const windowHeight = window.innerHeight;
    const windowAspect = windowWidth / windowHeight;

    let canvasWidth: number;
    let canvasHeight: number;

    if (windowAspect > GameScene.FIXED_ASPECT_RATIO) {
      // ウィンドウが横長 → 高さ基準で幅を計算
      canvasHeight = windowHeight;
      canvasWidth = windowHeight * GameScene.FIXED_ASPECT_RATIO;
    } else {
      // ウィンドウが縦長 → 幅基準で高さを計算
      canvasWidth = windowWidth;
      canvasHeight = windowWidth / GameScene.FIXED_ASPECT_RATIO;
    }

    return { width: canvasWidth, height: canvasHeight };
  }

  private handleResize(): void {
    // 16:9のアスペクト比を維持したキャンバスサイズを計算
    const { width, height } = this.calculateCanvasSize();
    this.renderer.setSize(width, height);

    // カメラのアスペクト比は常に16:9に固定
    this.camera.aspect = GameScene.FIXED_ASPECT_RATIO;
    this.camera.updateProjectionMatrix();
  }

  public render(): void {
    this.renderer.render(this.scene, this.camera);
  }

  public dispose(): void {
    window.removeEventListener('resize', this.handleResize.bind(this));
    this.renderer.dispose();
  }
}
